import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminAiStatus,
  AppliesTo,
  LeadRulesDto,
  LeadScoringResponse,
  RepScoringResponse,
  AdminCompanyRow,
  ConnectionTestRequest,
  ConnectionTestResponse,
  CreateCompanyRequest,
  ConversationsResponse,
  LeadJourneyResponse,
  MeResponse,
  NavCounts,
  NoteDto,
  OverviewResponse,
  RescueListResponse,
  ObjectionStatsResponse,
  ReviveListResponse,
  RescueStatus,
  Grade,
  UndoToken,
  TeamResponse,
  IntegrationsResponse,
  LeaderboardResponse,
  Period,
  JourneySetupResponse,
  JourneySaveRequest,
  CoachingListResponse,
  CoachingDetailResponse,
  AiMilestoneDto,
  ImportDays,
  AskRequest,
  AskResponse,
  WorkspaceSettingsDto,
  AiStart,
} from '@coachrex/shared';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { Accept: 'application/json', ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers },
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let code: string | undefined;
    try {
      const body = (await res.json()) as { error?: string; code?: string };
      message = body.error ?? message;
      code = body.code;
    } catch {
      /* not JSON */
    }
    // A session that ran out mid-use: the sign-in gate listens for this and shows the sign-in screen.
    if (res.status === 401 && code === 'SIGNED_OUT') window.dispatchEvent(new Event('coachrex:signed-out'));
    throw new ApiError(res.status, message, code);
  }
  return (await res.json()) as T;
}

export async function signOut() {
  await fetch('/auth/logout', { method: 'POST' });
  window.location.assign('/');
}

// Synced data changes in the background (the worker polls the CRM every few minutes), so lists refresh on an interval.
const LIVE = { refetchInterval: 60_000, refetchOnWindowFocus: true } as const;

export const useMe = () =>
  useQuery({
    queryKey: ['me'],
    queryFn: () => request<MeResponse>('/api/me'),
    staleTime: 5 * 60_000,
    // Signed out or no access is an answer, not a hiccup: show the sign-in screen right away.
    retry: (n, e) => !(e instanceof ApiError && (e.status === 401 || e.status === 403)) && n < 3,
  });

export const useNavCounts = () => useQuery({ queryKey: ['nav-counts'], queryFn: () => request<NavCounts>('/api/nav-counts'), ...LIVE });

export const useConversations = () =>
  useQuery({ queryKey: ['conversations'], queryFn: () => request<ConversationsResponse>('/api/conversations?days=7'), ...LIVE });

export const useLeadJourney = (id: string) =>
  useQuery({ queryKey: ['lead', id], queryFn: () => request<LeadJourneyResponse>(`/api/leads/${id}`), ...LIVE });

export function useAddNote(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => request<NoteDto>(`/api/leads/${leadId}/notes`, { method: 'POST', body: JSON.stringify({ text }) }),
    onSuccess: (note) => {
      qc.setQueryData<LeadJourneyResponse>(['lead', leadId], (prev) => (prev ? { ...prev, notes: [note, ...prev.notes] } : prev));
    },
  });
}

export const recordingUrl = (touchId: string) => `/api/touches/${touchId}/recording`;

// ---------- Rescue, Overview, grade changes ----------

export const useRescue = () => useQuery({ queryKey: ['rescue'], queryFn: () => request<RescueListResponse>('/api/rescue'), refetchInterval: 30_000, refetchOnWindowFocus: true });

export const useOverview = (rep: string) =>
  useQuery({
    queryKey: ['overview', rep],
    queryFn: () => request<OverviewResponse>(`/api/overview${rep === 'all' ? '' : `?rep=${rep === 'none' ? 'unassigned' : encodeURIComponent(rep)}`}`),
    ...LIVE,
  });

/** Anything a status or grade change can affect. */
function useRefreshAction() {
  const qc = useQueryClient();
  return () => Promise.all(['rescue', 'revive', 'overview', 'lead', 'nav-counts', 'conversations'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
}

export function useSetRescueStatus() {
  const refresh = useRefreshAction();
  return useMutation({
    mutationFn: (b: { id: string; status: RescueStatus; note: string }) =>
      request<UndoToken>(`/api/rescue/${b.id}/status`, { method: 'POST', body: JSON.stringify({ status: b.status, note: b.note }) }),
    onSuccess: refresh,
  });
}

export function useUndoRescue() {
  const refresh = useRefreshAction();
  return useMutation({ mutationFn: (undoId: string) => request<{ ok: true }>(`/api/rescue/undo/${undoId}`, { method: 'POST' }), onSuccess: refresh });
}

export function useAddToRescue(leadId: string) {
  const refresh = useRefreshAction();
  return useMutation({
    mutationFn: (note: string) => request<UndoToken>(`/api/leads/${leadId}/rescue`, { method: 'POST', body: JSON.stringify({ note }) }),
    onSuccess: refresh,
  });
}

export const useRevive = () => useQuery({ queryKey: ['revive'], queryFn: () => request<ReviveListResponse>('/api/revive'), refetchOnWindowFocus: true });

/** Sends a quiet lead to the Rescue queue. */
export function useReviveLead() {
  const refresh = useRefreshAction();
  return useMutation({
    mutationFn: (b: { leadId: string; note: string }) => request<UndoToken>(`/api/leads/${b.leadId}/revive`, { method: 'POST', body: JSON.stringify({ note: b.note }) }),
    onSuccess: refresh,
  });
}

export function useSkipRevive() {
  const refresh = useRefreshAction();
  return useMutation({
    mutationFn: (b: { leadId: string; reason: string }) => request<{ ok: true }>(`/api/leads/${b.leadId}/revive/skip`, { method: 'POST', body: JSON.stringify({ reason: b.reason }) }),
    onSuccess: refresh,
  });
}

export function useUnskipRevive() {
  const refresh = useRefreshAction();
  return useMutation({ mutationFn: (leadId: string) => request<{ ok: true }>(`/api/leads/${leadId}/revive/unskip`, { method: 'POST' }), onSuccess: refresh });
}

export function useChangeGrade(leadId: string) {
  const refresh = useRefreshAction();
  return useMutation({
    mutationFn: (b: { grade: Grade; note: string }) => request<UndoToken>(`/api/leads/${leadId}/grade`, { method: 'POST', body: JSON.stringify(b) }),
    onSuccess: refresh,
  });
}

export function useUndoGrade(leadId: string) {
  const refresh = useRefreshAction();
  return useMutation({
    mutationFn: (undoId: string) => request<{ ok: true }>(`/api/leads/${leadId}/grade/undo`, { method: 'POST', body: JSON.stringify({ undoId }) }),
    onSuccess: refresh,
  });
}

// ---------- Admin ----------


export const useAdminCompanies = () =>
  useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: () => request<AdminCompanyRow[]>('/api/admin/companies'),
    // Import progress moves while a backfill runs.
    refetchInterval: (q) => (q.state.data?.some((c) => c.importPct !== null) ? 10_000 : 60_000),
  });

export const useAdminCompany = (id: string | undefined) =>
  useQuery({
    queryKey: ['admin', 'company', id],
    enabled: !!id,
    queryFn: () => request<{ id: string; name: string; industry: string | null; status: string; locationId: string; importDays: ImportDays }>(`/api/admin/companies/${id}`),
  });

export function useSetImportDays(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days: ImportDays) => request<{ importDays: ImportDays }>(`/api/admin/companies/${id}/import-days`, { method: 'PUT', body: JSON.stringify({ days }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export const useTestConnection = () =>
  useMutation({ mutationFn: (body: ConnectionTestRequest) => request<ConnectionTestResponse>('/api/admin/test-connection', { method: 'POST', body: JSON.stringify(body) }) });

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCompanyRequest) => request<{ id: string; name: string }>('/api/admin/companies', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useFixConnection(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConnectionTestRequest) => request<{ id: string }>(`/api/admin/companies/${id}/connection`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useOpenCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<{ ok: true }>(`/api/admin/companies/${id}/open`, { method: 'POST' }),
    // Everything outside Admin belongs to the previous company now.
    onSuccess: () => qc.resetQueries(),
  });
}

export const useAdminAi = (id: string | undefined) =>
  useQuery({
    queryKey: ['admin', 'ai', id],
    enabled: !!id,
    queryFn: () => request<AdminAiStatus>(`/api/admin/companies/${id}/ai`),
    refetchInterval: 30_000,
  });

export function useSetAi(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => request<{ enabled: boolean }>(`/api/admin/companies/${id}/ai`, { method: 'PUT', body: JSON.stringify({ enabled }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ai', id] }),
  });
}

export function useSetAiStart(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (start: AiStart) => request<{ from: string; queued: number }>(`/api/admin/companies/${id}/ai/start`, { method: 'PUT', body: JSON.stringify({ start }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ai', id] }),
  });
}

export function useStartHistory(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days: number) => request<{ ok: true }>(`/api/admin/companies/${id}/history`, { method: 'POST', body: JSON.stringify({ days }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ai', id] }),
  });
}

// ---------- Scoring setup ----------

export const useRepScoring = () => useQuery({ queryKey: ['scoring', 'rep'], queryFn: () => request<RepScoringResponse>('/api/scoring/rep') });

export function useRepCriterionMutations() {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ['scoring', 'rep'] });
  return {
    add: useMutation({
      mutationFn: (body: { name: string; passDefinition: string; weight: number; appliesTo: AppliesTo }) =>
        request<{ id: string }>('/api/scoring/rep', { method: 'POST', body: JSON.stringify(body) }),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, ...body }: { id: string } & Partial<{ name: string; passDefinition: string; weight: number; appliesTo: AppliesTo; enabled: boolean }>) =>
        request<{ ok: true; rescored: number }>(`/api/scoring/rep/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: string) => request<{ ok: true }>(`/api/scoring/rep/${id}`, { method: 'DELETE' }),
      onSuccess: done,
    }),
  };
}

export const useLeadScoring = () => useQuery({ queryKey: ['scoring', 'lead'], queryFn: () => request<LeadScoringResponse>('/api/scoring/lead') });

export function useSaveLeadScoring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rules: LeadRulesDto) => request<{ version: number }>('/api/scoring/lead', { method: 'PUT', body: JSON.stringify(rules) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scoring', 'lead'] }),
  });
}

// ---------- Team & settings ----------

export const useTeam = () => useQuery({ queryKey: ['team'], queryFn: () => request<TeamResponse>('/api/team') });

/** Inactive users drop out of rep filters, the leaderboard and alerts, so those refresh too. */
export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { id: string; role?: 'manager' | 'rep'; active?: boolean }) =>
      request<{ ok: true }>(`/api/team/${b.id}`, { method: 'PATCH', body: JSON.stringify({ role: b.role, active: b.active }) }),
    onSuccess: () => Promise.all(['team', 'overview', 'rescue', 'conversations', 'me'].map((k) => qc.invalidateQueries({ queryKey: [k] }))),
  });
}

export function useSyncTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => request<TeamResponse>('/api/team/sync', { method: 'POST' }),
    onSuccess: (t) => qc.setQueryData(['team'], t),
  });
}

export const useSettings = () => useQuery({ queryKey: ['settings'], queryFn: () => request<WorkspaceSettingsDto>('/api/settings') });

export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: WorkspaceSettingsDto) => request<WorkspaceSettingsDto>('/api/settings', { method: 'PUT', body: JSON.stringify(b) }),
    onSuccess: (s) => {
      qc.setQueryData(['settings'], s);
      // Name, time zone, currency and the reply target show up across the app.
      return Promise.all(['me', 'overview', 'rescue', 'conversations', 'nav-counts', 'lead'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
    },
  });
}

// ---------- Integrations, Leaderboard, Journey setup ----------

export const useIntegrations = () =>
  useQuery({
    queryKey: ['integrations'],
    queryFn: () => request<IntegrationsResponse>('/api/integrations'),
    refetchInterval: (q) => (q.state.data?.importPct != null ? 10_000 : 60_000),
  });

export function useSyncNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => request<{ ok: true }>('/api/integrations/sync', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  });
}

export const useLeaderboard = (period: Period) =>
  useQuery({ queryKey: ['leaderboard', period], queryFn: () => request<LeaderboardResponse>(`/api/leaderboard?period=${period}`), ...LIVE });

export const useJourneySetup = () => useQuery({ queryKey: ['journey'], queryFn: () => request<JourneySetupResponse>('/api/journey') });

export function useSaveJourney() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: JourneySaveRequest) => request<{ ok: true; reevaluated: number }>('/api/journey', { method: 'PUT', body: JSON.stringify(b) }),
    onSuccess: () => Promise.all(['journey', 'lead', 'conversations', 'overview', 'rescue', 'integrations'].map((k) => qc.invalidateQueries({ queryKey: [k] }))),
  });
}

// ---------- Coaching ----------

export const useCoachingList = (period: Period) =>
  useQuery({ queryKey: ['coaching', period], queryFn: () => request<CoachingListResponse>(`/api/coaching?period=${period}`), ...LIVE });

export const useObjectionStats = (period: Period, rep: string | null) =>
  useQuery({
    queryKey: ['coaching', 'objections', period, rep],
    queryFn: () => request<ObjectionStatsResponse>(`/api/coaching/objections?period=${period}${rep ? `&rep=${encodeURIComponent(rep)}` : ''}`),
    ...LIVE,
  });

export const useCoachingDetail = (repId: string | null, period: Period) =>
  useQuery({
    queryKey: ['coaching', period, repId],
    enabled: !!repId,
    queryFn: () => request<CoachingDetailResponse>(`/api/coaching/${repId}?period=${period}`),
    ...LIVE,
  });

// ---------- "The AI hears it" milestones ----------

export const useAiMilestones = () => useQuery({ queryKey: ['ai-milestones'], queryFn: () => request<AiMilestoneDto[]>('/api/ai-milestones'), ...LIVE });

export function useRemoveAiMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<{ ok: true }>(`/api/ai-milestones/${id}/remove`, { method: 'POST' }),
    onSuccess: () => Promise.all(['ai-milestones', 'lead', 'journey', 'conversations', 'overview'].map((k) => qc.invalidateQueries({ queryKey: [k] }))),
  });
}

// ---------- Ask Lex ----------

export const useAsk = () => useMutation({ mutationFn: (b: AskRequest) => request<AskResponse>('/api/ask', { method: 'POST', body: JSON.stringify(b) }) });
